// ISO 3166-1 alpha-2 country codes (F8). The suite's one country list, shared by
// CountrySelect so OrgUnit addresses, person addresses and nationality all pick
// from the same set instead of free-typing a two-letter code.
//
// The codes are the data here; the NAMES are not. They used to be hard-coded
// German labels, which made the control wrong in the eight other product
// languages and sorted everything with a German collator. They are now derived
// at runtime from `Intl.DisplayNames` in the active locale (D13, issue 57):
// ~250 names x 9 locales in the message catalogues would be pure duplication of
// data every browser already ships, and most of those names are byte-identical
// to English in several of the languages — which is exactly what
// check-i18n-untranslated.mjs ratchets on.
export const COUNTRY_CODES: string[] = [
  'AF', 'EG', 'AX', 'AL', 'DZ', 'AS', 'AD', 'AO', 'AI', 'AQ', 'AG', 'GQ',
  'AR', 'AM', 'AW', 'AZ', 'ET', 'AU', 'BS', 'BH', 'BD', 'BB', 'BE', 'BZ',
  'BJ', 'BM', 'BT', 'BO', 'BQ', 'BA', 'BW', 'BV', 'BR', 'IO', 'BN', 'BG',
  'BF', 'BI', 'CV', 'CL', 'CN', 'CK', 'CR', 'CW', 'DK', 'DE', 'DM', 'DO',
  'DJ', 'EC', 'SV', 'CI', 'ER', 'EE', 'SZ', 'FK', 'FO', 'FJ', 'FI', 'FR',
  'GF', 'PF', 'TF', 'GA', 'GM', 'GE', 'GH', 'GI', 'GD', 'GR', 'GL', 'GP',
  'GU', 'GT', 'GG', 'GN', 'GW', 'GY', 'HT', 'HM', 'HN', 'HK', 'IN', 'ID',
  'IQ', 'IR', 'IE', 'IS', 'IM', 'IL', 'IT', 'JM', 'JP', 'YE', 'JE', 'JO',
  'KY', 'KH', 'CM', 'CA', 'KZ', 'QA', 'KE', 'KG', 'KI', 'CC', 'CO', 'KM',
  'CD', 'CG', 'KP', 'KR', 'HR', 'CU', 'KW', 'LA', 'LS', 'LV', 'LB', 'LR',
  'LY', 'LI', 'LT', 'LU', 'MO', 'MG', 'MW', 'MY', 'MV', 'ML', 'MT', 'MA',
  'MH', 'MQ', 'MR', 'MU', 'YT', 'MX', 'FM', 'MC', 'MN', 'ME', 'MS', 'MZ',
  'MM', 'NA', 'NR', 'NP', 'NC', 'NZ', 'NI', 'NL', 'NE', 'NG', 'NU', 'MK',
  'MP', 'NF', 'NO', 'OM', 'AT', 'PK', 'PS', 'PW', 'PA', 'PG', 'PY', 'PE',
  'PH', 'PN', 'PL', 'PT', 'PR', 'MD', 'RE', 'RW', 'RO', 'RU', 'SB', 'ZM',
  'WS', 'SM', 'ST', 'SA', 'SE', 'CH', 'SN', 'RS', 'SC', 'SL', 'ZW', 'SG',
  'SX', 'SK', 'SI', 'SO', 'ES', 'LK', 'BL', 'SH', 'KN', 'LC', 'MF', 'PM',
  'VC', 'ZA', 'SD', 'GS', 'SS', 'SR', 'SJ', 'SY', 'TJ', 'TW', 'TZ', 'TH',
  'TL', 'TG', 'TK', 'TO', 'TT', 'TD', 'CZ', 'TN', 'TR', 'TM', 'TC', 'TV',
  'UG', 'UA', 'HU', 'UY', 'UZ', 'VU', 'VA', 'VE', 'AE', 'US', 'GB', 'VG',
  'VI', 'UM', 'VN', 'WF', 'CX', 'EH', 'CF', 'CY',
];

export interface Country {
  code: string;
  label: string;
}

// `Intl.DisplayNames` construction is not free and this runs per keystroke in a
// sorted list of ~250, so one instance per locale is kept.
const displayNamesByLocale = new Map<string, Intl.DisplayNames | null>();

function displayNamesFor(locale: string): Intl.DisplayNames | null {
  if (!displayNamesByLocale.has(locale)) {
    let instance: Intl.DisplayNames | null = null;
    try {
      // `fallback: 'code'` means an unknown region comes back as its own code
      // rather than undefined — a country we cannot name is still selectable.
      instance = new Intl.DisplayNames([locale], { type: 'region', fallback: 'code' });
    } catch {
      // No Intl.DisplayNames (or no data for this locale): fall back to codes.
      instance = null;
    }
    displayNamesByLocale.set(locale, instance);
  }
  return displayNamesByLocale.get(locale) ?? null;
}

/** The country's name in `locale`, or the bare code when it cannot be named. */
export function countryName(code: string, locale: string): string {
  if (!code) return '';
  const upper = code.toUpperCase();
  try {
    return displayNamesFor(locale)?.of(upper) || upper;
  } catch {
    return upper;
  }
}

/**
 * Every country, named and sorted for `locale` — with that locale's collator, so
 * a Swedish list ends in a-ring/a-diaeresis/o-diaeresis and a German one files
 * "Agypten" under A.
 */
export function countryOptions(locale: string): Country[] {
  const collator = new Intl.Collator(locale);
  return COUNTRY_CODES.map((code) => ({ code, label: countryName(code, locale) })).sort((a, b) =>
    collator.compare(a.label, b.label),
  );
}
